import { Candidate, SearchRequest, CandidateMatch, JobDescription, SkillMatch } from '../shared/types';
import { mockCandidates } from './mockData';

export class FrontendSearchService {
  private candidates: Candidate[] = [];

  constructor() {
    this.candidates = mockCandidates;
    console.log(`✅ FrontendSearchService initialized with ${this.candidates.length} candidates`);
  }

  async searchCandidates(searchRequest: SearchRequest): Promise<CandidateMatch[]> {
    const startTime = Date.now();
    
    try {
      // Score and rank candidates
      const matches = await Promise.all(
        this.candidates.map(candidate => this.scoreCandidateMatch(candidate, searchRequest))
      );

      // Filter and sort by score
      const filteredMatches = matches
        .filter(match => this.passesFilters(match.candidate, searchRequest.filters))
        .sort((a, b) => b.score - a.score)
        .slice(0, searchRequest.limit || 10);

      const searchTime = Date.now() - startTime;
      console.log(`🔍 Frontend search completed in ${searchTime}ms, found ${filteredMatches.length} matches`);

      return filteredMatches;
    } catch (error) {
      console.error('Frontend search error:', error);
      throw new Error('Failed to search candidates');
    }
  }

  private async scoreCandidateMatch(
    candidate: Candidate, 
    searchRequest: SearchRequest
  ): Promise<CandidateMatch> {
    const { jobDescription } = searchRequest;
    
    // Simple semantic scoring without AI (using text matching)
    const semanticScore = this.calculateSimpleSemanticScore(
      candidate.resumeText,
      `${jobDescription.title} ${jobDescription.description}`
    );

    // Calculate skill matches
    const skillsMatch = this.calculateSkillMatch(
      candidate.skills,
      [...jobDescription.requirements, ...(jobDescription.preferredSkills || [])]
    );

    // Calculate experience match
    const experienceMatch = this.calculateExperienceMatch(candidate, jobDescription);

    // Calculate location match
    const locationMatch = this.calculateLocationMatch(candidate, jobDescription);

    // Calculate overall score (weighted)
    const weights = {
      semantic: 0.4,
      skills: 0.3,
      experience: 0.2,
      location: 0.1
    };

    const skillsScore = skillsMatch.reduce((acc, match) => acc + match.confidence, 0) / skillsMatch.length || 0;
    
    const totalScore = 
      (semanticScore * weights.semantic) +
      (skillsScore * weights.skills) +
      (experienceMatch * weights.experience) +
      (locationMatch ? 1 : 0.5) * weights.location;

    // Determine overall fit
    let overallFit: 'excellent' | 'good' | 'fair' | 'poor' = 'poor';
    if (totalScore >= 0.8) overallFit = 'excellent';
    else if (totalScore >= 0.6) overallFit = 'good';
    else if (totalScore >= 0.4) overallFit = 'fair';

    // Generate explanation
    const explanation = this.generateExplanation(candidate, skillsMatch, experienceMatch, overallFit);

    return {
      candidate,
      score: totalScore,
      matchDetails: {
        semanticScore,
        skillsMatch,
        experienceMatch,
        locationMatch,
        overallFit
      },
      explanation
    };
  }

  private calculateSimpleSemanticScore(candidateText: string, jobText: string): number {
    // Simple keyword matching implementation (no AI needed)
    const candidateWords = candidateText.toLowerCase().split(/\s+/);
    const jobWords = jobText.toLowerCase().split(/\s+/)
      .filter(word => word.length > 3) // Filter out short words
      .filter(word => !['the', 'and', 'with', 'that', 'this', 'have', 'will', 'from', 'they', 'been', 'were', 'are'].includes(word));

    if (jobWords.length === 0) return 0;

    const matches = jobWords.filter(jobWord => 
      candidateWords.some(candidateWord => 
        candidateWord.includes(jobWord) || jobWord.includes(candidateWord)
      )
    );

    return matches.length / jobWords.length;
  }

  private calculateSkillMatch(candidateSkills: string[], requiredSkills: string[]): SkillMatch[] {
    const matches: SkillMatch[] = [];
    
    requiredSkills.forEach(required => {
      const normalizedRequired = required.toLowerCase().trim();
      
      // Check for exact matches
      const exactMatch = candidateSkills.find(skill => 
        skill.toLowerCase().trim() === normalizedRequired
      );
      
      if (exactMatch) {
        matches.push({
          skill: required,
          match: 'exact',
          confidence: 1.0
        });
        return;
      }

      // Check for partial matches
      const partialMatch = candidateSkills.find(skill => 
        skill.toLowerCase().includes(normalizedRequired) || 
        normalizedRequired.includes(skill.toLowerCase())
      );
      
      if (partialMatch) {
        matches.push({
          skill: required,
          match: 'similar',
          confidence: 0.7
        });
        return;
      }

      // Check for related technologies (simplified)
      const relatedMatch = this.findRelatedSkill(normalizedRequired, candidateSkills);
      if (relatedMatch) {
        matches.push({
          skill: required,
          match: 'related',
          confidence: 0.5
        });
      }
    });

    return matches;
  }

  private findRelatedSkill(required: string, candidateSkills: string[]): boolean {
    const relatedSkills: { [key: string]: string[] } = {
      'javascript': ['typescript', 'node.js', 'react', 'vue', 'angular'],
      'typescript': ['javascript', 'node.js', 'react', 'vue', 'angular'],
      'react': ['javascript', 'typescript', 'vue', 'angular', 'next.js'],
      'python': ['django', 'flask', 'fastapi', 'pandas', 'numpy'],
      'java': ['spring', 'kotlin', 'android', 'maven', 'gradle'],
      'aws': ['azure', 'gcp', 'cloud', 'docker', 'kubernetes'],
      'docker': ['kubernetes', 'aws', 'devops', 'containerization']
    };

    const related = relatedSkills[required] || [];
    return candidateSkills.some(skill => 
      related.some(rel => skill.toLowerCase().includes(rel.toLowerCase()))
    );
  }

  private calculateExperienceMatch(candidate: Candidate, jobDescription: JobDescription): number {
    const experienceLevels = {
      'entry': { min: 0, max: 2 },
      'mid': { min: 2, max: 5 },
      'senior': { min: 5, max: 10 },
      'lead': { min: 8, max: 20 }
    };

    const required = experienceLevels[jobDescription.experienceLevel];
    const candidateYears = candidate.yearsOfExperience;

    if (candidateYears >= required.min && candidateYears <= required.max) {
      return 1.0;
    } else if (candidateYears >= required.min - 1 && candidateYears <= required.max + 2) {
      return 0.8;
    } else if (candidateYears >= required.min - 2 && candidateYears <= required.max + 4) {
      return 0.6;
    } else {
      return 0.3;
    }
  }

  private calculateLocationMatch(candidate: Candidate, jobDescription: JobDescription): boolean {
    if (jobDescription.remoteOk) return true;
    if (!jobDescription.location) return true;
    
    return candidate.location.toLowerCase().includes(jobDescription.location.toLowerCase()) ||
           jobDescription.location.toLowerCase().includes(candidate.location.toLowerCase());
  }

  private passesFilters(candidate: Candidate, filters: any): boolean {
    if (!filters) return true;

    // Experience level filter
    if (filters.experienceLevel) {
      const experienceMatch = this.calculateExperienceMatch(candidate, { 
        experienceLevel: filters.experienceLevel 
      } as JobDescription);
      if (experienceMatch < 0.6) return false;
    }

    // Years of experience filter
    if (filters.minYearsExperience && candidate.yearsOfExperience < filters.minYearsExperience) {
      return false;
    }
    if (filters.maxYearsExperience && candidate.yearsOfExperience > filters.maxYearsExperience) {
      return false;
    }

    // Skills filter
    if (filters.skills && filters.skills.length > 0) {
      const hasRequiredSkills = filters.skills.some((skill: string) =>
        candidate.skills.some(candidateSkill => 
          candidateSkill.toLowerCase().includes(skill.toLowerCase())
        )
      );
      if (!hasRequiredSkills) return false;
    }

    // Location filter
    if (filters.location && !filters.remoteOnly) {
      const locationMatch = candidate.location.toLowerCase()
        .includes(filters.location.toLowerCase());
      if (!locationMatch) return false;
    }

    return true;
  }

  private generateExplanation(
    candidate: Candidate, 
    skillsMatch: SkillMatch[], 
    experienceMatch: number,
    overallFit: string
  ): string {
    const exactSkills = skillsMatch.filter(m => m.match === 'exact').length;
    const totalSkills = skillsMatch.length;
    
    let explanation = `${candidate.name} is an ${overallFit} match. `;
    
    if (exactSkills > 0) {
      explanation += `Strong skill alignment with ${exactSkills}/${totalSkills} exact matches. `;
    }
    
    if (experienceMatch > 0.8) {
      explanation += `Experience level (${candidate.yearsOfExperience} years) perfectly fits requirements. `;
    } else if (experienceMatch > 0.6) {
      explanation += `Good experience match with ${candidate.yearsOfExperience} years. `;
    }
    
    explanation += `Background in ${candidate.title} with expertise in ${candidate.skills.slice(0, 3).join(', ')}.`;
    
    return explanation;
  }

  // Additional methods for other API endpoints
  getAllCandidates(params?: {
    limit?: number;
    offset?: number;
    skills?: string;
    location?: string;
  }) {
    let filteredCandidates = [...this.candidates];
    
    // Filter by skills if provided
    if (params?.skills) {
      const skillArray = params.skills.split(',').map(s => s.trim().toLowerCase());
      filteredCandidates = filteredCandidates.filter(candidate =>
        skillArray.some(skill =>
          candidate.skills.some(candidateSkill =>
            candidateSkill.toLowerCase().includes(skill)
          )
        )
      );
    }
    
    // Filter by location if provided
    if (params?.location) {
      filteredCandidates = filteredCandidates.filter(candidate =>
        candidate.location.toLowerCase().includes(params.location!.toLowerCase())
      );
    }
    
    // Apply pagination
    const startIndex = params?.offset || 0;
    const endIndex = startIndex + (params?.limit || 10);
    const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);
    
    return {
      candidates: paginatedCandidates,
      total: filteredCandidates.length,
      limit: params?.limit || 10,
      offset: startIndex
    };
  }

  getCandidateById(id: string): Candidate | null {
    return this.candidates.find(c => c.id === id) || null;
  }

  getUniqueSkills(): string[] {
    const allSkills = this.candidates.flatMap(candidate => candidate.skills);
    return Array.from(new Set(allSkills)).sort();
  }

  getUniqueLocations(): string[] {
    const allLocations = this.candidates.map(candidate => candidate.location);
    return Array.from(new Set(allLocations)).sort();
  }
}
